import React, { useEffect, useState } from "react";
import { customerApi, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, TrendingUp, TrendingDown, Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * Loyalty History Dialog — opened by clicking the points chip in the
 * top-right header of the Guest Portal. Buckets the ledger into
 * Earned / Redeemed / Expired so the guest can see where their points
 * came from and where they went.
 */
export default function LoyaltyHistoryDialog({ open, onOpenChange }) {
  const [data, setData] = useState({ balance: 0, earned: [], redeemed: [], expired: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    customerApi
      .get("/customers/me/loyalty/history")
      .then((r) => setData(r.data || { balance: 0, earned: [], redeemed: [], expired: [] }))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open]);

  const fmt = (n) => Number(n || 0).toLocaleString("en-GB");
  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
      });
    } catch { return iso; }
  };

  const renderRow = (r, tone) => (
    <div
      key={r.id}
      className="flex items-start justify-between gap-3 py-2.5 border-b border-border/50 last:border-b-0"
      data-testid={`loyalty-row-${r.id}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm truncate">{r.reason || "—"}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{fmtDate(r.at)}</p>
      </div>
      <div className={"text-sm tabular-nums font-medium shrink-0 " + tone}>
        {r.delta > 0 ? "+" : ""}{fmt(r.delta)} pts
      </div>
    </div>
  );

  const Section = ({ label, rows, tone, Icon, emptyText, testid }) => (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {label} <span className="text-foreground">({rows.length})</span>
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground italic py-2">{emptyText}</p>
      ) : (
        <div className="max-h-64 overflow-y-auto">
          {rows.map((r) => renderRow(r, tone))}
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="loyalty-history-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-600" />
            Loyalty history
          </DialogTitle>
          <DialogDescription>
            Current balance: <strong className="text-foreground" data-testid="loyalty-history-balance">
              {fmt(data.balance)} pts
            </strong>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : (
          <Tabs defaultValue="earned" className="mt-2">
            <TabsList className="grid grid-cols-3 w-full" data-testid="loyalty-history-tabs">
              <TabsTrigger value="earned" data-testid="loyalty-tab-earned">Earned</TabsTrigger>
              <TabsTrigger value="redeemed" data-testid="loyalty-tab-redeemed">Redeemed</TabsTrigger>
              <TabsTrigger value="expired" data-testid="loyalty-tab-expired">Expired</TabsTrigger>
            </TabsList>
            <TabsContent value="earned" className="mt-4">
              <Section
                label="Earned"
                rows={data.earned || []}
                tone="text-emerald-700"
                Icon={TrendingUp}
                emptyText="No points earned yet — book a treatment to start collecting."
                testid="loyalty-section-earned"
              />
            </TabsContent>
            <TabsContent value="redeemed" className="mt-4">
              <Section
                label="Redeemed"
                rows={data.redeemed || []}
                tone="text-rose-700"
                Icon={TrendingDown}
                emptyText="You haven't redeemed any points yet."
                testid="loyalty-section-redeemed"
              />
            </TabsContent>
            <TabsContent value="expired" className="mt-4">
              <Section
                label="Expired"
                rows={data.expired || []}
                tone="text-muted-foreground"
                Icon={Clock}
                emptyText="Nothing expired — nice work using your points on time."
                testid="loyalty-section-expired"
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
